import type { ResolvedRegion } from '@borradh-workspace/video-templates';

/**
 * Pure model for editing a draft's ON-SCREEN COPY — titles, list points,
 * calls to action, offer/card text — in the video editor.
 *
 * A v2 template reads its copy from exactly one of two draft keys:
 * `scriptRoles` (hook / body / cta / disclaimer / lists) or `scriptContent`
 * (a template's own declared fields, which is where offer and info cards get
 * their words). `PATCH :id/draft-config` refuses a copy patch the template
 * will not read, so the editor always edits — and saves — the key the draft
 * already carries.
 *
 * The preview is not recompiled for a text change: the compiled RenderDoc
 * carries each piece of copy verbatim (a numbered list only strips a leading
 * "1." — the renderer draws its own badge), so swapping old strings for new
 * ones previews exactly what the save will render.
 */

export interface ScriptRolesLike {
  hook: string;
  body: string[];
  cta?: string;
  disclaimer?: string;
  lists?: string[][];
}

export type ScriptContentLike = Record<string, unknown>;

export type DraftCopy =
  | { source: 'scriptRoles'; value: ScriptRolesLike }
  | { source: 'scriptContent'; value: ScriptContentLike };

type Path = (string | number)[];

export interface CopyField {
  /** Stable across renders; encodes the field's position. */
  id: string;
  label: string;
  path: Path;
  value: string;
  /** Set when the field is (part of) a list entry that can be reordered. */
  list?: { path: Path; index: number; length: number };
}

const LIST_NUMBER = /^\s*\d+\s*[.)\-–:]\s+/;

/** What a numbered list actually shows for an item. */
export const stripListNumber = (text: string) => text.replace(LIST_NUMBER, '');

/** The copy the draft's template reads, or `null` when it has none. */
export function draftCopyOf(
  draft:
    | { scriptRoles?: ScriptRolesLike; scriptContent?: ScriptContentLike }
    | null
    | undefined
): DraftCopy | null {
  if (draft?.scriptRoles) {
    return { source: 'scriptRoles', value: draft.scriptRoles };
  }
  if (draft?.scriptContent && Object.keys(draft.scriptContent).length > 0) {
    return { source: 'scriptContent', value: draft.scriptContent };
  }
  return null;
}

function humanize(key: string) {
  const words = key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return words.charAt(0).toUpperCase() + words.slice(1);
}

function singular(label: string) {
  return label.endsWith('s') && !label.endsWith('ss')
    ? label.slice(0, -1)
    : label;
}

/** Every editable piece of text, in reading order, with a plain label. */
export function copyFields(copy: DraftCopy): CopyField[] {
  const fields: CopyField[] = [];
  const add = (
    id: string,
    label: string,
    path: Path,
    value: string,
    list?: CopyField['list']
  ) => fields.push({ id, label, path, value, ...(list ? { list } : {}) });

  if (copy.source === 'scriptRoles') {
    const roles = copy.value;
    add('hook', 'Title', ['hook'], roles.hook ?? '');
    roles.body.forEach((text, i) =>
      add(`body.${i}`, `Point ${i + 1}`, ['body', i], text, {
        path: ['body'],
        index: i,
        length: roles.body.length,
      })
    );
    if (roles.cta !== undefined) {
      add('cta', 'Call to action', ['cta'], roles.cta);
    }
    if (roles.disclaimer !== undefined) {
      add('disclaimer', 'Disclaimer', ['disclaimer'], roles.disclaimer);
    }
    roles.lists?.forEach((list, l) =>
      list.forEach((text, i) =>
        add(
          `lists.${l}.${i}`,
          `List ${l + 1} · item ${i + 1}`,
          ['lists', l, i],
          text,
          { path: ['lists', l], index: i, length: list.length }
        )
      )
    );
    return fields;
  }

  for (const [key, value] of Object.entries(copy.value)) {
    const name = humanize(key);
    if (typeof value === 'string') {
      add(key, name, [key], value);
      continue;
    }
    if (!Array.isArray(value)) continue;
    value.forEach((row, i) => {
      const list = { path: [key], index: i, length: value.length };
      if (typeof row === 'string') {
        add(`${key}.${i}`, `${singular(name)} ${i + 1}`, [key, i], row, list);
        return;
      }
      if (row && typeof row === 'object') {
        for (const [sub, subValue] of Object.entries(row)) {
          if (typeof subValue !== 'string') continue;
          add(
            `${key}.${i}.${sub}`,
            `${singular(name)} ${i + 1} · ${humanize(sub).toLowerCase()}`,
            [key, i, sub],
            subValue,
            list
          );
        }
      }
    });
  }
  return fields;
}

function getAt(root: unknown, path: Path): unknown {
  let node = root;
  for (const key of path) {
    if (node === null || typeof node !== 'object') return undefined;
    node = (node as Record<string | number, unknown>)[key];
  }
  return node;
}

function setAt(root: unknown, path: Path, value: unknown): unknown {
  if (path.length === 0) return value;
  const [head, ...rest] = path;
  if (Array.isArray(root)) {
    const next = root.slice();
    next[head as number] = setAt(root[head as number], rest, value);
    return next;
  }
  const record = (root ?? {}) as Record<string, unknown>;
  return {
    ...record,
    [head]: setAt(record[head as string], rest, value),
  };
}

export function setCopyText(
  copy: DraftCopy,
  path: Path,
  text: string
): DraftCopy {
  return { ...copy, value: setAt(copy.value, path, text) } as DraftCopy;
}

/** Moves one list entry (a string, or a whole record row) to a new index. */
export function moveCopyListItem(
  copy: DraftCopy,
  listPath: Path,
  from: number,
  to: number
): DraftCopy {
  const list = getAt(copy.value, listPath);
  if (!Array.isArray(list) || to < 0 || to >= list.length || from === to) {
    return copy;
  }
  const next = list.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return { ...copy, value: setAt(copy.value, listPath, next) } as DraftCopy;
}

/**
 * original text → edited text, for every field whose words changed. Keyed by
 * field POSITION, so a reorder maps each slot's old words to its new ones.
 */
export function copyReplacements(
  original: DraftCopy | null,
  current: DraftCopy | null
): Map<string, string> {
  const replacements = new Map<string, string>();
  if (!original || !current) return replacements;
  const before = new Map(copyFields(original).map((f) => [f.id, f.value]));
  for (const field of copyFields(current)) {
    const old = before.get(field.id);
    if (old && old !== field.value) replacements.set(old, field.value);
  }
  return replacements;
}

/** Swaps every `text` in a compiled tree through `replacements` (matching a
 * numbered list's stripped form too). Returns the input untouched when there
 * is nothing to replace. */
export function applyTextReplacements<T>(
  node: T,
  replacements: ReadonlyMap<string, string>
): T {
  if (replacements.size === 0) return node;
  const stripped = new Map(
    [...replacements].map(([from, to]) => [
      stripListNumber(from),
      stripListNumber(to),
    ])
  );
  const walk = (value: unknown): unknown => {
    if (Array.isArray(value)) return value.map(walk);
    if (value && typeof value === 'object') {
      const out: Record<string, unknown> = {};
      for (const [key, child] of Object.entries(value)) {
        out[key] =
          key === 'text' && typeof child === 'string'
            ? (replacements.get(child) ?? stripped.get(child) ?? child)
            : walk(child);
      }
      return out;
    }
    return value;
  };
  return walk(node) as T;
}

/**
 * The first frame a piece of text is on screen: its block's start plus the
 * element's own entrance. `null` when the template does not show it.
 */
export function firstFrameOfText(
  root: ResolvedRegion,
  text: string
): number | null {
  if (!text) return null;
  const targets = new Set([text, stripListNumber(text)]);
  const offsetIn = (value: unknown): number | null => {
    if (Array.isArray(value)) {
      for (const child of value) {
        const found = offsetIn(child);
        if (found !== null) return found;
      }
      return null;
    }
    if (value && typeof value === 'object') {
      const record = value as Record<string, unknown>;
      if (typeof record.text === 'string' && targets.has(record.text)) {
        return typeof record.entranceFrame === 'number'
          ? record.entranceFrame
          : 0;
      }
      for (const child of Object.values(record)) {
        const found = offsetIn(child);
        if (found !== null) return found;
      }
    }
    return null;
  };

  let best: number | null = null;
  const visit = (region: ResolvedRegion) => {
    if (region.kind === 'leaf') {
      for (const block of [...region.spine, ...region.overlays]) {
        const offset = offsetIn(block);
        if (offset === null) continue;
        const frame = block.startFrame + offset;
        best = best === null ? frame : Math.min(best, frame);
      }
      return;
    }
    for (const child of region.children) visit(child.region);
  };
  visit(root);
  return best;
}
